// src/config.ts
// CRA injects every REACT_APP_* var into process.env at build-time.
const isDevelopment = process.env.NODE_ENV === 'development';
const isLocalhost = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' || 
    window.location.hostname === '127.0.0.1'
);

const defaultAPI = (isDevelopment || isLocalhost) 
    ? "http://127.0.0.1:8000" 
    : "https://brandcomply.fly.dev";

export const API_ROOT = process.env.REACT_APP_API_ROOT || defaultAPI;

// Project API functions
export interface Project {
  id: number;
  user_id: number;
  name: string;
  created_at: string;
}

export interface ProjectCreate {
  name: string;
}

export interface ProjectUpdate {
  name: string;
}

export const projectAPI = {
  // Create a new project
  create: async (projectData: ProjectCreate): Promise<Project> => {
    const token = localStorage.getItem("token");
    const response = await fetch(`${API_ROOT}/projects`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify(projectData)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to create project: ${response.statusText}`);
    }
    
    return response.json();
  },

  // Get all projects for the current user
  list: async (): Promise<Project[]> => {
    const token = localStorage.getItem("token");
    const response = await fetch(`${API_ROOT}/projects`, {
      headers: {
        "Authorization": `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch projects: ${response.statusText}`);
    }
    
    return response.json();
  },

  // Get a specific project
  get: async (projectId: number): Promise<Project> => {
    const token = localStorage.getItem("token");
    const response = await fetch(`${API_ROOT}/projects/${projectId}`, {
      headers: {
        "Authorization": `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch project: ${response.statusText}`);
    }
    
    return response.json();
  },

  // Update a project
  update: async (projectId: number, projectData: ProjectUpdate): Promise<Project> => {
    const token = localStorage.getItem("token");
    const response = await fetch(`${API_ROOT}/projects/${projectId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify(projectData)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update project: ${response.statusText}`);
    }
    
    return response.json();
  },

  // Delete a project
  delete: async (projectId: number): Promise<void> => {
    const token = localStorage.getItem("token");
    const response = await fetch(`${API_ROOT}/projects/${projectId}`, {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete project: ${response.statusText}`);
    }
  },

  // Get analyses for a specific project
  getAnalyses: async (projectId: number): Promise<any[]> => {
    const token = localStorage.getItem("token");
    const response = await fetch(`${API_ROOT}/projects/${projectId}/analyses`, {
      headers: {
        "Authorization": `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch project analyses: ${response.statusText}`);
    }
    
    return response.json();
  }
};

// Analysis API functions
export interface Analysis {
  unique_id: string;
  campaignName: string;
  created_at: number;
  project_id?: number;
}

export interface AnalysisUpdate {
  campaign_name?: string;
  project_id?: number;
}

export const analysisAPI = {
  // Update an analysis (rename or move to different project)
  update: async (uniqueId: string, analysisData: AnalysisUpdate): Promise<Analysis> => {
    const token = localStorage.getItem("token");
    const response = await fetch(`${API_ROOT}/analyses/${uniqueId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify(analysisData)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update analysis: ${response.statusText}`);
    }
    
    return response.json();
  },

  // Delete an analysis
  delete: async (uniqueId: string): Promise<void> => {
    const token = localStorage.getItem("token");
    const response = await fetch(`${API_ROOT}/analyses/${uniqueId}`, {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete analysis: ${response.statusText}`);
    }
  }
};